import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { MaintenanceDefect, TechNoteEntry, UnscheduledEvent } from '../types';
import type { TriageErtsAction } from '../services/triageService';
import {
  AtaCodeRecord,
  fetchAtaCodes,
  filterAtaCodesByAircraftType,
} from '../services/ataCodeService';
import {
  FleetRecord,
  fetchFleetData,
  getAircraftTypeByTail,
  getOperatorByTail,
  getTailNumbers,
  isSkyWrenchSupportedCarrier,
} from '../services/fleetService';
import { createEmptyEvent } from './eventModal/createEmptyEvent';
import { buildMovedEvent } from './eventModal/buildMovedEvent';
import {
  applyAuditAliases,
  buildErtsCommitEntries,
  normalizeDamageFields,
  nowZuluTimestamp,
  aliasFromEmail,
} from './eventModal/saveHelpers';
import DueDateSection from './eventModal/DueDateSection';
import { getSaveDisabledReasons } from './eventModal/saveValidation';
import { computeRequiredFlags } from './eventModal/requiredFields';
import AircraftGeneralSection from './eventModal/AircraftGeneralSection';
import DeferredSection from './eventModal/DeferredSection';
import StdSection from './eventModal/StdSection';
import EventModalFooter from './eventModal/EventModalFooter';
import ModalHeaderAliases from './eventModal/ModalHeaderAliases';
import {
  EVENT_STATUS,
  MOVE_EVENT_OPTION,
  ERTS_INITIAL_CHANGE_REASON,
  ERTS_FINAL_CHANGE_REASON,
  ERTS_FINAL_CONFIDENCE,
  ERTS_REMOVED_CONFIDENCE,
} from '../constants';
import BarriersSection, { MaintenanceBarrier } from './eventModal/BarriersSection';
import DefectsSection from './eventModal/DefectsSection';
import NotesSection from './eventModal/NotesSection';
import IropsSection from './eventModal/IropsSection';
import ErtsSection from './eventModal/ErtsSection';
import TimeDeclaredSection from './eventModal/TimeDeclaredSection';
import SummarySection from './eventModal/SummarySection';
import AuditModal, { AuditNoteEntry } from './AuditModal';
import TechNotesModal from './TechNotesModal';
import { useAuth } from '../auth';
import { useProjectedErts } from './eventModal/useProjectedErts';
import { useLiveNextFlight } from './eventModal/useLiveNextFlight';
import { useSkyDentDamageStatus } from '../hooks/useDamageLocationStatus';
import { buildOpeningSnapshot } from './eventModal/openingSnapshot';
import { getFlightAutoPopulate } from '../services/flightService';
import { zuluToEpochSeconds } from './eventModal/dateTimeUtils';
import {
  DefectCompletionSyncResult,
  syncDefectCompletionsWithFinalErts,
} from './eventModal/defect-completion';
import { applyNormalFlights, applyIropFlights } from './eventModal/flightAutoPopulate';
import { useSkyWrench } from './eventModal/useSkyWrench';
import { useAtaClassification } from './eventModal/useAtaClassification';
import { generateSummary, concatNotesForSummary } from '../services/summaryService';

/**
 * Props for the EventModal component.
 */
interface EventModalVerificationFields {
  status: boolean;
  timeDeclaredDate: boolean;
  timeDeclaredTime: boolean;
}

interface EventModalProps {
  event: UnscheduledEvent | null;
  mode: 'view' | 'edit' | 'create';
  isOpen: boolean;
  onClose: () => void;
  onSave: (updated: UnscheduledEvent) => void;
  /**
   * Invoked when the user moves the record to another record type. `source` is
   * the original record closed as COMPLETED with its `moveStatus` set;
   * `destination` is the pre-populated, unsaved event for the new record type.
   */
  onMove?: (source: UnscheduledEvent, destination: UnscheduledEvent) => void;
  recordType?: string;
  onRecordTypeChange?: (type: string) => void;
  events?: UnscheduledEvent[];
  /** True once a completed-history request has settled, so history searches are conclusive. */
  completedHistoryResolved?: boolean;
  /** Active (IN_WORK/PENDING) events, used to block duplicate In-Work records. */
  activeEvents?: UnscheduledEvent[];
  /** Active-list projection displayed independently from the persisted event shape. */
  projectedErtsOverlay?: Record<number, string>;
  /** Explicit ERTS change supplied by Triage, preserving manual-edit semantics. */
  triageErtsAction?: TriageErtsAction;
  /** Whether the update target had an ERTS before Triage staged its value. */
  triageErtsExistedBefore?: boolean;
  /** Treats values staged by a Triage UPDATE as pending changes. */
  hasStagedTriageChanges?: boolean;
  /** New Triage defects that require a Knowledge Base ATA recommendation. */
  triageRecommendationDefectIds?: number[];
  /** Triage-populated fields not shown in the review window that need verification. */
  verificationFields?: EventModalVerificationFields;
}

/**
 * Modal popup for viewing or editing an unscheduled event.
 * Layout matches the reference screenshot with labeled field rows.
 */
const EventModal: React.FC<EventModalProps> = ({
  event,
  mode,
  isOpen,
  onClose,
  onSave,
  onMove,
  recordType,
  onRecordTypeChange,
  events = [],
  completedHistoryResolved = false,
  activeEvents = [],
  projectedErtsOverlay,
  triageErtsAction = 'none',
  triageErtsExistedBefore = false,
  hasStagedTriageChanges = false,
  triageRecommendationDefectIds = [],
  verificationFields,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<UnscheduledEvent | null>(null);
  // Destination record type chosen when the "Move Event" status action is active.
  const [moveTargetType, setMoveTargetType] = useState<string>('');
  const [ataCodes, setAtaCodes] = useState<AtaCodeRecord[]>([]);
  const [fleetData, setFleetData] = useState<FleetRecord[]>([]);
  const [selectedChapters, setSelectedChapters] = useState<Record<number, string>>({});
  const [selectedSections, setSelectedSections] = useState<Record<number, string>>({});
  const { projectedErts } = useProjectedErts(isOpen, event, projectedErtsOverlay);
  const [isErtsHistoryOpen, setIsErtsHistoryOpen] = useState<boolean>(false);
  const [isDueDateHistoryOpen, setIsDueDateHistoryOpen] = useState<boolean>(false);
  const [dueDateChangeReason, setDueDateChangeReason] = useState<string>('');
  const [dueDateChangeReasonOther, setDueDateChangeReasonOther] = useState<string>('');
  const [isDeaBarriersOpen, setIsDeaBarriersOpen] = useState<boolean>(false);
  const [damageCode, setDamageCode] = useState<string>('');
  const [barriers, setBarriers] = useState<MaintenanceBarrier[]>([]);
  const [barrierToEdit, setBarrierToEdit] = useState<MaintenanceBarrier | null>(null);
  const [defects, setDefects] = useState<MaintenanceDefect[]>([]);
  const [noteEntries, setNoteEntries] = useState<
    { id: string; date: string; time: string; alias: string; defectIds: number[]; text: string }[]
  >([]);
  const [ertsEdited, setErtsEdited] = useState<boolean>(false);
  const [ertsConfidence, setErtsConfidence] = useState<string>('');
  const [ertsChangeReason, setErtsChangeReason] = useState<string>('');
  const [ertsChangeReasonOther, setErtsChangeReasonOther] = useState<string>('');
  const [ertsHistory, setErtsHistory] = useState<
    {
      ertsDate: string;
      ertsTime: string;
      confidence: string;
      changeReason: string;
      submittedBy: string;
      submittedAt: string;
    }[]
  >([]);
  const [isAuditOpen, setIsAuditOpen] = useState<boolean>(false);
  const [isTechNotesOpen, setIsTechNotesOpen] = useState<boolean>(false);
  const [auditCompleted, setAuditCompleted] = useState<boolean>(false);
  const [summaryRequired, setSummaryRequired] = useState<boolean>(false);
  const [summaryCompleted, setSummaryCompleted] = useState<boolean>(false);
  const [summarizing, setSummarizing] = useState<boolean>(false);
  const [manualOverride, setManualOverride] = useState<boolean>(false);
  const [defectHistory, setDefectHistory] = useState<
    Record<
      number,
      { eventId: string; erts: string; defectText: string; correctiveAction: string }[]
    >
  >({});
  const { skyWrenchResults, triggerSkyWrench } = useSkyWrench({
    isOpen,
    event,
    formData,
    defects,
    selectedChapters,
    selectedSections,
    fleetData,
  });
  const [auditEntries, setAuditEntries] = useState<AuditNoteEntry[]>([]);
  const [deletedAuditFindings, setDeletedAuditFindings] = useState<string[]>([]);
  // Waiting Paperwork lives here (not inside AuditModal) so it survives the
  // audit popup closing and is available on the event save, where it is
  // snapshotted onto each audit finding.
  const [waitingPaperworkDate, setWaitingPaperworkDate] = useState<string>('');
  const [waitingPaperworkTime, setWaitingPaperworkTime] = useState<string>('');
  // Snapshot of the record state when the modal opened, used to detect edits so
  // the Update button is only enabled when something actually changed.
  const initialSnapshotRef = useRef<string>('');
  // Tracks the most recently requested tail so a slow flight lookup that
  // resolves after the tail changed again is ignored (no stale writes).
  const flightLookupTailRef = useRef<string>('');
  // Ensures a Triage CREATE triggers its initial APES flight lookup once after
  // Time Declared, Tail, and the asynchronously loaded fleet are all ready.
  const triageFlightAutoPopulateStartedRef = useRef<boolean>(false);
  // The Due Date (YYYY-MM-DD) when the modal opened — used to detect a Deferred
  // Due Date change so a history entry is logged (like ERTS) on save.
  const openingDueDateRef = useRef<string>('');
  // Defect IDs present at modal open are not manual additions. Triage defects
  // are staged before opening, so they remain on the immediate request path.
  const openingDefectIdsRef = useRef<Set<number>>(new Set<number>());
  // Tracks defect completions created by this modal so they continue following
  // Final ERTS edits without overwriting completion values that predated it.
  const autoPopulatedDefectIdsRef = useRef<Set<number>>(new Set<number>());
  const syncDefectCompletions = useCallback((finalErtsZulu: string): void => {
    setDefects((currentDefects: MaintenanceDefect[]): MaintenanceDefect[] => {
      const result: DefectCompletionSyncResult = syncDefectCompletionsWithFinalErts(
        currentDefects,
        finalErtsZulu,
        autoPopulatedDefectIdsRef.current,
      );
      autoPopulatedDefectIdsRef.current = result.autoPopulatedDefectIds;
      return result.defects;
    });
  }, []);
  const [manualRecommendationDefectIds, setManualRecommendationDefectIds] = useState<number[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    flightLookupTailRef.current = '';
    triageFlightAutoPopulateStartedRef.current = false;
  }, [event, mode, isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    autoPopulatedDefectIdsRef.current.clear();
  }, [event, mode, isOpen]);

  // Loads the record's own data into the form and captures the opening snapshot
  // used for the dirty check. A create with no incoming event starts empty.
  const hydrateRecordData = useCallback(
    (incoming: UnscheduledEvent | null, currentMode: 'view' | 'edit' | 'create'): void => {
      if (incoming) {
        setFormData({ ...incoming });
        setDefects(incoming.defects || []);
        setNoteEntries(incoming.noteEntries || []);
        setBarriers(incoming.barriers || []);
        setErtsHistory(incoming.ertsHistory || []);
        setDefectHistory(incoming.defectHistory || {});
        setDamageCode(incoming.damageCode || '');
        // Capture the opening state so we can detect whether the user edited anything.
        initialSnapshotRef.current = buildOpeningSnapshot(incoming);
        return;
      }
      if (currentMode !== 'create') return;
      setFormData(createEmptyEvent());
      setDefects([]);
      setNoteEntries([]);
      setBarriers([]);
      setErtsHistory([]);
      setDefectHistory({});
      setDamageCode('');
      initialSnapshotRef.current = '';
    },
    [],
  );

  // Clears per-event editing state (popups, ERTS/Due Date ceremony selections)
  // without touching record data, and re-baselines the Due Date change check.
  const resetEditingState = useCallback((incoming: UnscheduledEvent | null): void => {
    setMoveTargetType('');
    setErtsEdited(false);
    setErtsConfidence('');
    setErtsChangeReason('');
    setErtsChangeReasonOther('');
    setIsDeaBarriersOpen(false);
    setIsErtsHistoryOpen(false);
    setIsDueDateHistoryOpen(false);
    setIsTechNotesOpen(false);
    setDueDateChangeReason('');
    setDueDateChangeReasonOther('');
    openingDueDateRef.current = incoming?.dueDateZulu ? incoming.dueDateZulu.split(' ')[0] : '';
  }, []);

  // A completed event was necessarily audited and summarized before it could
  // be closed, so on (re)open its Audit/Summary requirements are already
  // satisfied — the buttons read blue rather than red. They only revert to red
  // when a change this session re-triggers an existing required-field rule
  // (e.g. editing notes re-requires the Summary).
  const applyOpeningReviewState = useCallback(
    (incoming: UnscheduledEvent | null, isTriageCompletion: boolean): void => {
      const openingCompleted: boolean = Boolean(
        incoming && incoming.status === EVENT_STATUS.COMPLETED,
      );
      const openingTriageCompletion: boolean = openingCompleted && isTriageCompletion;
      setAuditCompleted(openingCompleted && !openingTriageCompletion);
      setSummaryRequired(openingTriageCompletion);
      setSummaryCompleted(openingCompleted && !openingTriageCompletion);
      setSummarizing(false);
      setManualOverride(false);
      setSelectedChapters(incoming?.selectedChapters || {});
      setSelectedSections(incoming?.selectedSections || {});
      setAuditEntries(incoming?.auditEntries || []);
      setDeletedAuditFindings(incoming?.deletedAuditFindings || []);
      setWaitingPaperworkDate('');
      setWaitingPaperworkTime('');
    },
    [],
  );

  // Synchronize the incoming event before paint so a Triage draft never shows
  // an empty or previously opened form while the modal-opening state settles.
  useLayoutEffect(() => {
    if (!isOpen) return;
    hydrateRecordData(event, mode);
    resetEditingState(event);
    applyOpeningReviewState(event, Boolean(verificationFields));
  }, [
    event,
    mode,
    isOpen,
    verificationFields,
    hydrateRecordData,
    resetEditingState,
    applyOpeningReviewState,
  ]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    openingDefectIdsRef.current = new Set<number>(
      (event?.defects || []).map((defect: MaintenanceDefect): number => defect.id),
    );
    setManualRecommendationDefectIds([]);
  }, [event, isOpen]);

  // A Triage-supplied ERTS action follows the same state transition as a
  // manual ERTS edit. Existing ERTS values require the reviewer to select a
  // new reason/confidence; explicit removals use the established removed state.
  useLayoutEffect(() => {
    if (!isOpen || triageErtsAction === 'none') return;
    const currentRecordType: string = recordType || event?.recordType || '';
    if (currentRecordType === 'Deferred') return;

    if (triageErtsAction === 'set') {
      if (!event?.returnToServiceTimeZulu) return;
      const isInitialErts: boolean = !triageErtsExistedBefore;
      setFormData((current) =>
        current && isInitialErts
          ? { ...current, initialReturnToServiceTimeZulu: event.returnToServiceTimeZulu }
          : current,
      );
      setErtsEdited(true);
      setErtsChangeReason(isInitialErts ? ERTS_INITIAL_CHANGE_REASON : '');
      setErtsConfidence('');
      setErtsChangeReasonOther('');
      return;
    }

    if (triageErtsExistedBefore) {
      setErtsEdited(true);
      setErtsChangeReason('');
      setErtsConfidence(ERTS_REMOVED_CONFIDENCE);
      setErtsChangeReasonOther('');
    }
  }, [event, isOpen, recordType, triageErtsAction, triageErtsExistedBefore]);

  // Completion detected in a reviewed Triage note follows the same Final ERTS
  // transition as manually selecting Completed. The verification-fields object
  // identifies this as a Triage draft rather than a reopened completed event.
  useLayoutEffect(() => {
    if (!isOpen || !verificationFields || event?.status !== EVENT_STATUS.COMPLETED) return;
    const currentRecordType: string = recordType || event.recordType || '';
    if (currentRecordType === 'Deferred') return;

    syncDefectCompletions(event.returnToServiceTimeZulu);
    setErtsEdited(true);
    setErtsChangeReason(ERTS_FINAL_CHANGE_REASON);
    setErtsConfidence(ERTS_FINAL_CONFIDENCE);
    setErtsChangeReasonOther('');
    // Match Next Advise to the ERTS so a Completed triage record can't trip the
    // "Next Advise cannot be after the ERTS" rule.
    setFormData((current) =>
      current && current.returnToServiceTimeZulu
        ? { ...current, nextAdviseTimeZulu: current.returnToServiceTimeZulu }
        : current,
    );
  }, [event, isOpen, recordType, syncDefectCompletions, verificationFields]);

  // Keep the (read-only, system-managed) Next Scheduled Flight fields live for
  // existing events, refreshing on open and on an interval while open;
  // Previous/Chargeable stay frozen.
  useLiveNextFlight(isOpen, event, setFormData, initialSnapshotRef);

  // Triage-created records use record-type status defaults and keep them aligned
  // if the reviewer changes the record type in this final verification window.
  // Normal Deferred creates retain their existing Pending default behavior.
  useEffect(() => {
    if (mode !== 'create') return;
    if (verificationFields?.status) {
      const triageStatus: string =
        recordType === 'Unscheduled' ? EVENT_STATUS.IN_WORK : EVENT_STATUS.PENDING;
      setFormData((previous) => {
        if (!previous || previous.status === EVENT_STATUS.COMPLETED) return previous;
        return previous.status !== triageStatus ? { ...previous, status: triageStatus } : previous;
      });
      return;
    }
    if (recordType === 'Deferred') {
      setFormData((previous) =>
        previous && !previous.status ? { ...previous, status: EVENT_STATUS.PENDING } : previous,
      );
    }
  }, [mode, recordType, verificationFields?.status]);

  // Load ATA codes for defect chapter/section dropdowns
  useEffect(() => {
    if (isOpen) {
      fetchAtaCodes().then(setAtaCodes);
    }
  }, [isOpen]);

  // Load fleet data (Beta Tail List) for the tail number dropdown
  useEffect(() => {
    if (isOpen) {
      fetchFleetData().then(setFleetData);
    }
  }, [isOpen]);

  const availableAtaCodes: AtaCodeRecord[] = filterAtaCodesByAircraftType(
    ataCodes,
    formData?.aircraftType || getAircraftTypeByTail(fleetData, formData?.tailNumber || ''),
  );

  const triageRecommendationRecordType: string =
    mode === 'create' ? recordType || formData?.recordType || '' : formData?.recordType || '';
  const currentManualRecommendationDefectIds: number[] = useMemo(
    (): number[] =>
      manualRecommendationDefectIds.filter((defectId: number): boolean =>
        defects.some((defect: MaintenanceDefect): boolean => defect.id === defectId),
      ),
    [defects, manualRecommendationDefectIds],
  );
  const recommendationDefectIds: number[] = useMemo(
    (): number[] =>
      Array.from(
        new Set<number>([
          ...triageRecommendationDefectIds,
          ...currentManualRecommendationDefectIds,
        ]),
      ),
    [currentManualRecommendationDefectIds, triageRecommendationDefectIds],
  );
  const {
    pendingDefectIds: pendingAtaRecommendationDefectIds,
    unconfirmedDefectIds: unconfirmedAtaRecommendationDefectIds,
    failedDefectIds: failedAtaRecommendationDefectIds,
    settledDefectIds: settledAtaRecommendationDefectIds,
    acknowledgeRecommendation: acknowledgeAtaRecommendation,
    discardRecommendation: discardAtaRecommendation,
  } = useAtaClassification({
    isOpen,
    mode,
    recordType: triageRecommendationRecordType,
    triageRecommendationDefectIds: recommendationDefectIds,
    formData,
    defects,
    fleetData,
    availableAtaCodes,
    selectedChapters,
    selectedSections,
    setSelectedChapters,
    setSelectedSections,
  });

  // A manual create starts flight auto-population from Tail Number onChange.
  // Triage already supplies Tail and Time Declared, so wait for fleet readiness
  // and invoke that same APES mapping once without requiring a manual re-entry.
  useEffect(() => {
    if (!isOpen || mode !== 'create' || !verificationFields?.status || !formData) return;
    if (triageFlightAutoPopulateStartedRef.current) return;
    const currentRecordType: string = recordType || formData.recordType || '';
    if (currentRecordType === 'Scheduled' || currentRecordType === 'Deferred') return;
    const { tailNumber, timeDeclaredZulu } = formData;
    const at: number | undefined = zuluToEpochSeconds(timeDeclaredZulu);
    if (!tailNumber || at === undefined || !getTailNumbers(fleetData).includes(tailNumber)) return;

    const iropSelected: boolean = Boolean(
      formData.returnToBlock ||
        formData.abortedTakeOff ||
        formData.airTurnBack ||
        formData.diversion,
    );
    const updateDraftField = (
      field: keyof UnscheduledEvent,
      value: string | number | boolean,
    ): void => setFormData((current) => (current ? { ...current, [field]: value } : current));

    triageFlightAutoPopulateStartedRef.current = true;
    flightLookupTailRef.current = tailNumber;
    // Carrier comes solely from the fleet operator for the tail (normalized),
    // not the per-flight APES carrier or the email-extracted carrier.
    const fleetCarrier = getOperatorByTail(fleetData, tailNumber);
    if (fleetCarrier) updateDraftField('carrier', fleetCarrier);
    void getFlightAutoPopulate(tailNumber, at).then((result) => {
      if (!result || flightLookupTailRef.current !== tailNumber) return;
      if (iropSelected) applyIropFlights(result, updateDraftField);
      else applyNormalFlights(result, updateDraftField);
    });
  }, [fleetData, formData, isOpen, mode, recordType, verificationFields?.status]);

  // SkyDent Damage Location status for the current event. Drives the completion
  // requirement that a Completed damage record must have its (SkyDent-owned)
  // Damage Location filled in before it can be updated.
  const skyDentDamageStatus = useSkyDentDamageStatus(
    event?.eventId,
    formData?.status === EVENT_STATUS.COMPLETED && !!formData?.damage,
  );

  if (!isOpen || !formData) return null;

  const isReadOnly = mode === 'view' || (mode === 'create' && !recordType);
  const triageCompletionDetected: boolean = Boolean(
    verificationFields && formData.status === EVENT_STATUS.COMPLETED,
  );
  const damageLocationRequiredMissing = Boolean(
    formData.status === EVENT_STATUS.COMPLETED &&
      formData.damage &&
      skyDentDamageStatus?.exists &&
      skyDentDamageStatus?.locationMissing,
  );
  const effectiveRecordType = mode === 'create' ? recordType : formData.recordType || '';
  const isScheduled = effectiveRecordType === 'Scheduled';
  const isPlanned = effectiveRecordType === 'Planned';
  const isDeferred = effectiveRecordType === 'Deferred';
  const triageRecommendationDefects: number[] = triageRecommendationDefectIds.filter(
    (defectId: number): boolean =>
      defects.some((defect: { id: number; text: string }): boolean => defect.id === defectId),
  );
  const triageRecommendationPending: boolean = triageRecommendationDefects.some(
    (defectId: number): boolean => pendingAtaRecommendationDefectIds.includes(defectId),
  );
  const triageRecommendationUnconfirmed: boolean = triageRecommendationDefects.some(
    (defectId: number): boolean => unconfirmedAtaRecommendationDefectIds.includes(defectId),
  );
  const triageRecommendationFailed: boolean = triageRecommendationDefects.some(
    (defectId: number): boolean => failedAtaRecommendationDefectIds.includes(defectId),
  );
  const verifyFlightAutoPopulateFields: boolean = Boolean(
    verificationFields?.status && !isScheduled && !isDeferred,
  );
  const hasVerificationFields: boolean = Boolean(
    verificationFields &&
      (verificationFields.status ||
        verificationFields.timeDeclaredDate ||
        verificationFields.timeDeclaredTime),
  );

  // Time Declared must have BOTH a date and a valid Zulu time (HH:MM) before the
  // dependent fields (Tail Number, Status, Next Advise) are activated on create.
  const timeDeclared = formData.timeDeclaredZulu;
  const timeDeclaredComplete =
    !!timeDeclared &&
    timeDeclared.includes(' ') &&
    (timeDeclared.split(' ')[1]?.replace(/[^0-9]/g, '').length ?? 0) >= 4;
  const awaitingTimeDeclared = mode === 'create' && !timeDeclaredComplete;

  // Whether this record had an ERTS before opening — drives the ERTS removal
  // UX so the "Removed ERTS" confidence is available after a staged removal.
  const ertsExisted =
    triageErtsAction === 'remove'
      ? triageErtsExistedBefore
      : Boolean(
          event &&
            (event.returnToServiceTimeZulu || (event.ertsHistory && event.ertsHistory.length > 0)),
        );

  // Block creating a second In-Work Unscheduled event for a tail that already
  // has one — duplicate active events for the same aircraft are not allowed.
  const duplicateInWorkBlocked =
    mode === 'create' &&
    effectiveRecordType === 'Unscheduled' &&
    !!formData.tailNumber &&
    activeEvents.some(
      (e) =>
        (e.recordType || 'Unscheduled') === 'Unscheduled' &&
        e.tailNumber === formData.tailNumber &&
        e.status === EVENT_STATUS.IN_WORK,
    );

  // A new Unscheduled/Planned record requires the full set of details (next
  // advise, classified defects, defect history, a note) before it can be
  // created. Drives required-field highlighting in the child sections.
  // Field-level "required" flags for red highlighting, mirroring saveValidation
  // so the highlight clears exactly when each field stops blocking the button.
  const {
    tailRequired,
    defectsRequired,
    notesRequired: notesRequiredFlag,
    ataRequired,
    nextAdviseRequired,
    ertsRequired,
    scheduleDateRequired,
    stationRequired,
    deferredFieldsRequired,
    historyRequired: defectHistoryRequired,
  } = computeRequiredFlags({
    isReadOnly,
    mode,
    recordType: effectiveRecordType || '',
    isScheduled,
    isDeferred,
    isDeleted: formData.status === EVENT_STATUS.DELETED,
  });

  // Whether the user has changed anything since the modal opened (edit mode).
  // Triage UPDATE values are staged before the opening snapshot is captured, so
  // they are explicitly treated as pending changes. In-modal edits are compared
  // against the composite opening snapshot as usual.
  const currentSnapshot = JSON.stringify({
    form: formData,
    defects,
    noteEntries,
    barriers,
    ertsHistory,
    defectHistory,
    selectedChapters,
    selectedSections,
    auditEntries,
  });
  const isDirty: boolean =
    hasStagedTriageChanges ||
    ertsEdited ||
    (initialSnapshotRef.current !== '' && currentSnapshot !== initialSnapshotRef.current);

  // Move mode: the user selected the "Move Event" status action. Only offered
  // for an active record open in edit mode. While active, a destination
  // record-type picker replaces the normal save gating (Update activates once a
  // target type is chosen).
  const isMoveMode = formData.status === MOVE_EVENT_OPTION;
  const canMoveRecord =
    mode === 'edit' &&
    (event?.status === EVENT_STATUS.IN_WORK || event?.status === EVENT_STATUS.PENDING);
  const manualDefectIds: number[] = defects
    .filter(
      (defect: MaintenanceDefect): boolean =>
        Boolean(defect.text.trim()) &&
        !openingDefectIdsRef.current.has(defect.id) &&
        !triageRecommendationDefectIds.includes(defect.id),
    )
    .map((defect: MaintenanceDefect): number => defect.id);
  const canRequestManualRecommendation: boolean =
    mode !== 'view' &&
    !isScheduled &&
    !isMoveMode &&
    availableAtaCodes.length > 0 &&
    Boolean(formData.tailNumber) &&
    Boolean(formData.carrier || getOperatorByTail(fleetData, formData.tailNumber)) &&
    Boolean(formData.aircraftType || getAircraftTypeByTail(fleetData, formData.tailNumber));
  const unclassifiedDefectIds: number[] = defects
    .filter(
      (defect: MaintenanceDefect): boolean =>
        !selectedChapters[defect.id] || !selectedSections[defect.id],
    )
    .map((defect: MaintenanceDefect): number => defect.id);
  const manualRecommendationAwaiting: boolean =
    canRequestManualRecommendation &&
    currentManualRecommendationDefectIds.some(
      (defectId: number): boolean => !settledAtaRecommendationDefectIds.includes(defectId),
    );
  const manualRecommendationUnconfirmed: boolean = currentManualRecommendationDefectIds.some(
    (defectId: number): boolean => unconfirmedAtaRecommendationDefectIds.includes(defectId),
  );
  const manualRecommendationFailed: boolean = currentManualRecommendationDefectIds.some(
    (defectId: number): boolean => failedAtaRecommendationDefectIds.includes(defectId),
  );
  const manualRecommendationUnavailable: boolean =
    !canRequestManualRecommendation &&
    currentManualRecommendationDefectIds.some((defectId: number): boolean =>
      unclassifiedDefectIds.includes(defectId),
    );

  /**
   * Fetches previous/next flights for a known fleet tail and fills the flight
   * fields. `iropSelected` (any of RTB/ATO/ATB) shifts the mapping back one:
   * the previous flight becomes Chargeable + Next Scheduled, and the flight
   * before it becomes Previous. Anchored to the event's Time Declared; skipped
   * for Scheduled/Deferred records and unknown/partial tails. Called on tail
   * change and on RTB/ATO/ATB toggle. Stale responses are dropped.
   */
  const populateFlights = (tail: string, iropSelected: boolean): void => {
    flightLookupTailRef.current = tail;
    if (isScheduled || isDeferred || !getTailNumbers(fleetData).includes(tail)) return;
    const at = zuluToEpochSeconds(formData.timeDeclaredZulu);
    void getFlightAutoPopulate(tail, at).then((result) => {
      if (!result || flightLookupTailRef.current !== tail) return;
      // Carrier is sourced solely from the fleet operator (seeded on tail
      // change); the per-flight APES carrier no longer overrides it.
      if (iropSelected) applyIropFlights(result, updateField);
      else applyNormalFlights(result, updateField);
    });
  };

  const updateField = (field: keyof UnscheduledEvent, value: string | number | boolean): void => {
    setFormData((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [field]: value };
      // Completing a (non-Deferred) event pins Next Advise to the ERTS: a
      // Completed record's Next Advise may not be after its ERTS, so matching
      // them prevents the "Next Advise cannot be after the ERTS" mismatch that
      // would otherwise block the update.
      if (
        field === 'status' &&
        value === EVENT_STATUS.COMPLETED &&
        !isDeferred &&
        next.returnToServiceTimeZulu
      ) {
        next.nextAdviseTimeZulu = next.returnToServiceTimeZulu;
      }
      return next;
    });
    // Leaving move mode (picking a real status again) clears the chosen target.
    if (field === 'status' && value !== MOVE_EVENT_OPTION) setMoveTargetType('');
    if (field === 'status' && value !== EVENT_STATUS.COMPLETED) {
      autoPopulatedDefectIdsRef.current.clear();
    }
    // Completion can be selected before Final ERTS is fully entered. Retry on
    // each ERTS edit; the helper waits until both date and time are complete.
    if (
      field === 'returnToServiceTimeZulu' &&
      typeof value === 'string' &&
      formData.status === EVENT_STATUS.COMPLETED &&
      !isDeferred
    ) {
      syncDefectCompletions(value);
    }
    // Completing a (non-Deferred) event finalizes its ERTS: the change reason
    // and confidence auto-populate to Final ERTS / Final, and the edit is
    // committed to ERTS history on save. Deferred records track a Due Date, not
    // an ERTS, so they are excluded.
    if (field === 'status' && value === EVENT_STATUS.COMPLETED && !isDeferred) {
      syncDefectCompletions(formData.returnToServiceTimeZulu);
      setErtsEdited(true);
      setErtsChangeReason(ERTS_FINAL_CHANGE_REASON);
      setErtsConfidence(ERTS_FINAL_CONFIDENCE);
      setErtsChangeReasonOther('');
    }
  };

  const handleTechNotesChange = (entries: TechNoteEntry[]): void => {
    setFormData((current: UnscheduledEvent | null): UnscheduledEvent | null =>
      current ? { ...current, techNotes: entries } : current,
    );
  };

  /**
   * Stamps the audit aliases on a record from the current user's email alias.
   */
  const stampAliases = (record: UnscheduledEvent): void => {
    applyAuditAliases(record, user?.email);
  };

  /**
   * Builds the updated ERTS history when ERTS was edited this session, persists
   * the latest confidence/reason on the record, and resets ERTS edit state.
   * Returns the (possibly unchanged) history array.
   */
  const commitErtsHistory = (record: UnscheduledEvent): typeof ertsHistory => {
    if (!(ertsEdited && ertsConfidence && ertsChangeReason)) return ertsHistory;
    const newEntries = buildErtsCommitEntries({
      record,
      confidence: ertsConfidence,
      changeReason: ertsChangeReason,
      changeReasonOther: ertsChangeReasonOther,
      email: user?.email,
      existingHistory: ertsHistory,
    });

    const updated = [...ertsHistory, ...newEntries];
    setErtsHistory(updated);
    const finalEntry = newEntries[newEntries.length - 1];
    record.returnToServiceConfidence = ertsConfidence;
    record.returnToServiceChangeReason = finalEntry.changeReason;
    // When completion synthesizes an Initial ERTS row, mirror the value into the
    // Initial ERTS field so the form's read-only Initial ERTS display matches.
    if (!record.initialReturnToServiceTimeZulu && record.returnToServiceTimeZulu) {
      record.initialReturnToServiceTimeZulu = record.returnToServiceTimeZulu;
    }
    setErtsEdited(false);
    setErtsConfidence('');
    setErtsChangeReason('');
    setErtsChangeReasonOther('');
    return updated;
  };

  /**
   * Logs a Deferred record's Due Date change into the history (reusing the
   * record-type-agnostic ERTS-history array/table). The first Due Date on a new
   * record is logged as the initial value; a later change uses the selected
   * change reason. Returns the (possibly unchanged) history array.
   */
  const commitDueDateHistory = (record: UnscheduledEvent): typeof ertsHistory => {
    const currentDue = record.dueDateZulu ? record.dueDateZulu.split(' ')[0] : '';
    if (!currentDue || currentDue === openingDueDateRef.current) return ertsHistory;
    const isInitial = ertsHistory.length === 0;
    const reason = isInitial
      ? 'Initial Due Date'
      : dueDateChangeReason === 'Other'
        ? dueDateChangeReasonOther
        : dueDateChangeReason;
    const entry = {
      ertsDate: currentDue,
      ertsTime: '',
      confidence: '',
      changeReason: reason,
      submittedBy: aliasFromEmail(user?.email),
      submittedAt: nowZuluTimestamp(),
    };
    const updated = [...ertsHistory, entry];
    setErtsHistory(updated);
    openingDueDateRef.current = currentDue;
    setDueDateChangeReason('');
    setDueDateChangeReasonOther('');
    return updated;
  };

  /**
   * Summary button: concatenate all notes, send them to the summary tool (via
   * the backend), drop the returned summary into the textarea, and mark the
   * summary gate complete so the user can save. Guards against re-entry while a
   * request is in flight.
   */
  const handleSummary = async (): Promise<void> => {
    if (!formData || summarizing) return;
    const notes = concatNotesForSummary(noteEntries);
    if (!notes.trim()) {
      // Nothing to summarize — still clear the gate so save isn't blocked.
      setSummaryCompleted(true);
      return;
    }
    setSummarizing(true);
    try {
      const { summary } = await generateSummary(
        formData.eventId || '',
        notes,
        formData.summary || '',
      );
      updateField('summary', summary);
      setSummaryCompleted(true);
    } catch {
      // Surface a non-blocking hint in the textarea; leave the gate unset so the
      // user can retry. (Logged in the service.)
      window.alert('Summary generation failed. Please try again.');
    } finally {
      setSummarizing(false);
    }
  };

  const shouldBlockEventSave = (): boolean => {
    const saveReasonsExcludingAtaSelection: string[] = standardSaveDisabledReasons.filter(
      (reason: string): boolean => reason !== 'Select an ATA chapter and section for every defect.',
    );
    const ataSelectionRequired: boolean = standardSaveDisabledReasons.includes(
      'Select an ATA chapter and section for every defect.',
    );
    if (
      saveReasonsExcludingAtaSelection.length > 0 ||
      ataSelectionRequired ||
      triageRecommendationPending ||
      triageRecommendationUnconfirmed ||
      triageRecommendationFailed ||
      manualRecommendationAwaiting ||
      manualRecommendationUnconfirmed ||
      manualRecommendationFailed
    )
      return true;
    return manualRecommendationUnavailable;
  };

  const handleSave = (): void => {
    if (!formData || shouldBlockEventSave()) return;
    // Deferred records track the Due Date in the history (like ERTS); every
    // other record type tracks ERTS. The two are mutually exclusive per type.
    const updatedErtsHistory =
      effectiveRecordType === 'Deferred'
        ? commitDueDateHistory(formData)
        : commitErtsHistory(formData);

    // Persist all data on the event.
    formData.defects = defects;
    formData.noteEntries = noteEntries;
    formData.barriers = barriers;
    formData.ertsHistory = updatedErtsHistory;
    formData.recordType = effectiveRecordType;
    formData.selectedChapters = selectedChapters;
    formData.selectedSections = selectedSections;
    delete formData.projectedErts;
    formData.defectHistory = defectHistory;
    formData.skyWrenchResults = skyWrenchResults;
    formData.auditEntries = auditEntries;
    formData.deletedAuditFindings = deletedAuditFindings;
    // Carry the Audit modal's Waiting Paperwork onto the save so the backend
    // snapshots it onto each audit finding (stripped from the event row itself).
    formData.waitingPaperworkDate = waitingPaperworkDate;
    formData.waitingPaperworkTime = waitingPaperworkTime;
    normalizeDamageFields(formData, damageCode);
    stampAliases(formData);

    onSave(formData);
  };

  /**
   * Executes a move: closes the source record as COMPLETED with its `moveStatus`
   * set to the chosen destination type, then hands the caller a pre-populated,
   * unsaved destination event (with all notes/defects/barriers carried over) to
   * open in a Create Event window.
   */
  const handleMove = (): void => {
    if (!formData || !moveTargetType || !onMove) return;
    const originalRecordType = effectiveRecordType || 'Unscheduled';
    const originalStatus = event?.status || EVENT_STATUS.IN_WORK;

    // Commit any in-progress ERTS / Due-Date edit into history, exactly as a
    // normal completion save would (the source is being closed as COMPLETED).
    const updatedErtsHistory =
      originalRecordType === 'Deferred'
        ? commitDueDateHistory(formData)
        : commitErtsHistory(formData);

    // Assemble and close the source record.
    const source: UnscheduledEvent = {
      ...formData,
      defects,
      noteEntries,
      barriers,
      ertsHistory: updatedErtsHistory,
      recordType: originalRecordType,
      selectedChapters,
      selectedSections,
      defectHistory,
      skyWrenchResults,
      auditEntries,
      deletedAuditFindings,
      status: EVENT_STATUS.COMPLETED,
      moveStatus: moveTargetType,
    };
    delete source.projectedErts;
    normalizeDamageFields(source, damageCode);
    stampAliases(source);

    // Build the destination from the source's pre-move (active) state so the new
    // record carries the original status rather than COMPLETED.
    const destination = buildMovedEvent({ ...source, status: originalStatus }, moveTargetType);
    onMove(source, destination);
  };

  const saveGateState = {
    mode,
    recordType: effectiveRecordType,
    formData,
    defects,
    noteEntries,
    barriers,
    auditEntries,
    selectedChapters,
    selectedSections,
    requiredAtaDefectIds: manualDefectIds,
    defectHistory,
    isDirty,
    auditCompleted,
    summaryRequired,
    summaryCompleted,
    damageCode,
    ertsEdited,
    ertsConfidence,
    ertsChangeReason,
    duplicateInWorkBlocked,
    originalNextAdvise: event?.nextAdviseTimeZulu,
    dueDateChangedFromExisting:
      effectiveRecordType === 'Deferred' &&
      mode === 'edit' &&
      !!openingDueDateRef.current &&
      (formData.dueDateZulu ? formData.dueDateZulu.split(' ')[0] : '') !==
        openingDueDateRef.current,
    dueDateChangeReason,
    damageLocationRequiredMissing,
  };
  // A move closes the source as COMPLETED, so before it can be moved the record
  // must satisfy every completion requirement (evaluated as if COMPLETED) AND a
  // destination record type must be chosen.
  const standardSaveDisabledReasons: string[] = isMoveMode
    ? [
        ...(moveTargetType ? [] : ['Select a record type to move to.']),
        ...getSaveDisabledReasons({
          ...saveGateState,
          isMoveClose: true,
          formData: { ...formData, status: EVENT_STATUS.COMPLETED },
        }),
      ]
    : getSaveDisabledReasons(saveGateState);
  const saveDisabledReasons: string[] = Array.from(
    new Set<string>([
      ...standardSaveDisabledReasons,
      ...(triageRecommendationPending
        ? ['Knowledge Base is determining the ATA recommendation.']
        : []),
      ...(triageRecommendationUnconfirmed
        ? ['Confirm each Knowledge Base ATA recommendation.']
        : []),
      ...(triageRecommendationFailed
        ? [
            'Knowledge Base ATA recommendation was unavailable. Manually select Chapter and Section to continue.',
          ]
        : []),
      ...(manualRecommendationAwaiting
        ? ['Knowledge Base is determining the ATA recommendation.']
        : []),
      ...(manualRecommendationUnconfirmed
        ? ['Confirm each Knowledge Base ATA recommendation.']
        : []),
      ...(manualRecommendationFailed
        ? [
            'Knowledge Base ATA recommendation was unavailable. Manually select Chapter and Section to continue.',
          ]
        : []),
      ...(manualRecommendationUnavailable
        ? [
            'Knowledge Base ATA recommendation is unavailable. Manually select Chapter and Section to continue.',
          ]
        : []),
    ]),
  );

  return (
    <div className="modal-overlay active" role="dialog" aria-modal="true">
      <div className="modal event-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {mode === 'view'
              ? 'View Event'
              : mode === 'create'
                ? `New ${recordType || ''} Record`
                : 'Edit Event'}
          </h2>
          <ModalHeaderAliases formData={formData} />
          <button className="modal-close" onClick={onClose} type="button" aria-label="Close">
            &times;
          </button>
        </div>

        <div className="modal-body-scroll">
          {hasVerificationFields && (
            <div className="modal-verification-banner" role="note">
              Fields outlined in yellow were populated from Triage and should be verified.
            </div>
          )}
          {duplicateInWorkBlocked && (
            <div className="modal-warning-banner" role="alert">
              An In-Work Unscheduled event already exists for tail {formData.tailNumber}. A second
              In-Work Unscheduled event for this aircraft cannot be created.
            </div>
          )}
          {/* Record Type - only shown in create mode */}
          {mode === 'create' && (
            <div className="field-row">
              <label className="field-label">Record Type</label>
              <div className="field-group">
                <select
                  className={`field-input record-type-modal-select${!recordType ? ' field-required-missing' : ''}`}
                  value={recordType || ''}
                  onChange={(e) => {
                    if (onRecordTypeChange) {
                      onRecordTypeChange(e.target.value);
                    }
                  }}
                >
                  <option value="">Select Type...</option>
                  <option value="Unscheduled">Unscheduled</option>
                  <option value="Planned">Planned</option>
                  <option value="Deferred">Deferred</option>
                  <option value="Scheduled">Scheduled</option>
                </select>
              </div>
            </div>
          )}

          <TimeDeclaredSection
            formData={formData}
            isReadOnly={isReadOnly}
            updateField={updateField}
            label={isScheduled ? 'Schedule Date' : undefined}
            dateRequired={scheduleDateRequired}
            verifyDate={verificationFields?.timeDeclaredDate}
            verifyTime={verificationFields?.timeDeclaredTime}
          />

          <AircraftGeneralSection
            formData={formData}
            mode={mode}
            isReadOnly={isReadOnly}
            isScheduled={isScheduled}
            isDeferred={isDeferred}
            fleetData={fleetData}
            updateField={updateField}
            populateFlights={populateFlights}
            tailRequired={tailRequired}
            stationRequired={stationRequired}
            awaitingTimeDeclared={awaitingTimeDeclared}
            verifyStatus={Boolean(verificationFields?.status || triageCompletionDetected)}
            verifyFlightFields={verifyFlightAutoPopulateFields}
            canMove={canMoveRecord}
            currentRecordType={effectiveRecordType || ''}
            moveTargetType={moveTargetType}
            onMoveTargetChange={setMoveTargetType}
          />

          {!isDeferred && (
            <ErtsSection
              formData={formData}
              isReadOnly={isReadOnly}
              updateField={updateField}
              ertsEdited={ertsEdited}
              setErtsEdited={setErtsEdited}
              ertsConfidence={ertsConfidence}
              setErtsConfidence={setErtsConfidence}
              ertsChangeReason={ertsChangeReason}
              setErtsChangeReason={setErtsChangeReason}
              ertsChangeReasonOther={ertsChangeReasonOther}
              setErtsChangeReasonOther={setErtsChangeReasonOther}
              ertsHistory={ertsHistory}
              isErtsHistoryOpen={isErtsHistoryOpen}
              setIsErtsHistoryOpen={setIsErtsHistoryOpen}
              ertsExisted={ertsExisted}
              nextAdviseRequired={nextAdviseRequired}
              awaitingTimeDeclared={awaitingTimeDeclared}
              originalNextAdvise={event?.nextAdviseTimeZulu}
              ertsRequired={ertsRequired}
            />
          )}

          {isDeferred && (
            <DueDateSection
              formData={formData}
              mode={mode}
              isReadOnly={isReadOnly}
              updateField={updateField}
              dueDateHistory={ertsHistory}
              isDueDateHistoryOpen={isDueDateHistoryOpen}
              setIsDueDateHistoryOpen={setIsDueDateHistoryOpen}
              dueDateChangeReason={dueDateChangeReason}
              setDueDateChangeReason={setDueDateChangeReason}
              dueDateChangeReasonOther={dueDateChangeReasonOther}
              setDueDateChangeReasonOther={setDueDateChangeReasonOther}
              openingDueDate={openingDueDateRef.current}
            />
          )}

          {/* Next Scheduled Flight — hidden for Scheduled and Deferred records */}
          {!isScheduled && !isDeferred && (
            <StdSection
              formData={formData}
              mode={mode}
              verifyFields={verifyFlightAutoPopulateFields}
            />
          )}

          {!isDeferred && (
            <IropsSection
              formData={formData}
              isReadOnly={isReadOnly}
              damageCode={damageCode}
              setDamageCode={setDamageCode}
              updateField={updateField}
              populateFlights={populateFlights}
              isPlanned={isPlanned}
            />
          )}

          {isDeferred && (
            <DeferredSection
              formData={formData}
              isReadOnly={isReadOnly}
              updateField={updateField}
              fieldsRequired={deferredFieldsRequired}
            />
          )}

          <BarriersSection
            barriers={barriers}
            setBarriers={setBarriers}
            mode={mode}
            isDeaBarriersOpen={isDeaBarriersOpen}
            setIsDeaBarriersOpen={setIsDeaBarriersOpen}
            defects={defects}
            timeDeclaredZulu={formData.timeDeclaredZulu}
            returnToServiceTimeZulu={formData.returnToServiceTimeZulu}
            isCompleted={formData.status === EVENT_STATUS.COMPLETED}
            barrierToEdit={barrierToEdit}
            onBarrierEditHandled={() => setBarrierToEdit(null)}
          />

          <DefectsSection
            defects={defects}
            setDefects={setDefects}
            mode={mode}
            isReadOnly={isReadOnly}
            barriers={barriers}
            ataCodes={availableAtaCodes}
            selectedChapters={selectedChapters}
            setSelectedChapters={setSelectedChapters}
            selectedSections={selectedSections}
            setSelectedSections={setSelectedSections}
            projectedErts={projectedErts}
            hideProjectedErts={isPlanned || isDeferred || isScheduled}
            defectHistory={defectHistory}
            setDefectHistory={setDefectHistory}
            completedEvents={events}
            completedHistoryResolved={completedHistoryResolved}
            historySourceEvent={event}
            tailNumber={formData.tailNumber}
            requiresClassification={ataRequired}
            requiredAtaDefectIds={manualDefectIds}
            defectsRequired={defectsRequired}
            historyRequired={defectHistoryRequired}
            skyWrenchResults={isScheduled ? undefined : skyWrenchResults}
            onSkyWrenchTrigger={isScheduled ? undefined : triggerSkyWrench}
            unconfirmedAtaRecommendationDefectIds={unconfirmedAtaRecommendationDefectIds}
            onAtaRecommendationAcknowledged={acknowledgeAtaRecommendation}
            onDefectCreated={(defectId: number): void => {
              setManualRecommendationDefectIds((previous: number[]): number[] =>
                previous.includes(defectId) ? previous : [...previous, defectId],
              );
            }}
            skyWrenchSupported={isSkyWrenchSupportedCarrier(formData.carrier)}
            onEditBarrier={(barrier) => setBarrierToEdit(barrier)}
            onDeleteBarrier={(barrier) =>
              setBarriers((prev) => prev.filter((b) => b.id !== barrier.id))
            }
            onDeleteDefect={(defectId) => {
              setDefects((prev) => prev.filter((d) => d.id !== defectId));
              setBarriers((prev) => prev.filter((b) => b.defectId !== defectId));
              setDefectHistory((prev) => {
                const next = { ...prev };
                delete next[defectId];
                return next;
              });
              setSelectedChapters((prev) => {
                const next = { ...prev };
                delete next[defectId];
                return next;
              });
              setSelectedSections((prev) => {
                const next = { ...prev };
                delete next[defectId];
                return next;
              });
              setManualRecommendationDefectIds((previous: number[]): number[] =>
                previous.filter((candidateId: number): boolean => candidateId !== defectId),
              );
              discardAtaRecommendation(defectId);
              setNoteEntries((prev) =>
                prev.map((note) => ({
                  ...note,
                  defectIds: note.defectIds.filter((id) => id !== defectId),
                })),
              );
            }}
          />

          <NotesSection
            noteEntries={noteEntries}
            setNoteEntries={(updater) => {
              setNoteEntries(updater);
              if (formData.status === EVENT_STATUS.COMPLETED) {
                setSummaryRequired(true);
                setSummaryCompleted(false);
              }
            }}
            defectsCount={defects.length}
            mode={mode}
            notesRequired={notesRequiredFlag}
            timeDeclaredZulu={formData.timeDeclaredZulu}
            returnToServiceTimeZulu={formData.returnToServiceTimeZulu}
            isCompleted={formData.status === EVENT_STATUS.COMPLETED}
            allowNotesBeforeTimeDeclared={isDeferred}
          />

          <SummarySection
            formData={formData}
            mode={mode}
            isReadOnly={isReadOnly}
            summaryCompleted={summaryCompleted}
            manualOverride={manualOverride}
            setManualOverride={setManualOverride}
            setSummaryCompleted={setSummaryCompleted}
            updateField={updateField}
            treatAsCompleted={isMoveMode}
          />
        </div>

        {/* Footer Actions */}
        <EventModalFooter
          mode={mode}
          formData={formData}
          treatAsCompleted={isMoveMode}
          summaryRequired={summaryRequired}
          summaryCompleted={summaryCompleted}
          auditCompleted={auditCompleted}
          saveDisabled={saveDisabledReasons.length > 0}
          saveDisabledReasons={saveDisabledReasons}
          onClose={onClose}
          onSummary={handleSummary}
          summarizing={summarizing}
          onSave={isMoveMode ? handleMove : handleSave}
          onOpenAudit={() => setIsAuditOpen(true)}
          onOpenTechNotes={() => setIsTechNotesOpen(true)}
        />
      </div>

      {/* Audit Modal */}
      {formData && (
        <AuditModal
          event={formData}
          isOpen={isAuditOpen}
          auditActive={isMoveMode}
          onClose={() => setIsAuditOpen(false)}
          onAuditComplete={() => setAuditCompleted(true)}
          barriers={barriers}
          auditEntries={auditEntries}
          onAuditEntriesChange={setAuditEntries}
          deletedFindings={deletedAuditFindings}
          onDeletedFindingsChange={setDeletedAuditFindings}
          noteEntries={noteEntries}
          waitingPaperworkDate={waitingPaperworkDate}
          waitingPaperworkTime={waitingPaperworkTime}
          setWaitingPaperworkDate={setWaitingPaperworkDate}
          setWaitingPaperworkTime={setWaitingPaperworkTime}
        />
      )}

      <TechNotesModal
        isOpen={isTechNotesOpen}
        onClose={() => setIsTechNotesOpen(false)}
        entries={formData.techNotes || []}
        onEntriesChange={handleTechNotesChange}
      />
    </div>
  );
};

export default EventModal;
